import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";
import { createWirePayment } from "../../../../KlashaWire/wiresAPI";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { nextVariants } from "../../../../Wallets/utils";
import {
  DetailsBox,
  DetailsContent,
  DetailsLabel,
  DetailsRow,
  DetailsSectionHeader,
  DetailsValue,
  EditButton,
  Heading,
  InputWrapper,
  PayoutWrapper,
  SectionTitle,
} from "../../styles";
import { Button } from "../../../../../components/common/Button";
import { formatCurrencyCodeAmount } from "../../../../../utils/currency";
import WalletOTP from "../../../../Wallets/components/SendFunds/components/WalletOTP";
import { sendOTP } from "../../../../../api/usersAPI";
import Loader from "../../../../../components/common/Loader";
import { merchantBankAccounts, merchantBusinessKeys } from "../../../../Settings/settingsSlice";
import { createUpdateBankAccount } from "../../../../Settings/settingsAPI";
import { useRequestPayout } from "../../../../../app/queries/settlements";
import { debitWallet, sendFunds } from "../../api";
import { SuccessAlertContext } from "../../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import { encryptAlgorithm } from "../../../../../utils/encryptionAlgorithm";
import { LocalVAPayoutMethods, settlementMethodCurrencyMap } from "../../../../Settlements/utils";
import { clearSettlementDetails } from "../../SettlementPayoutSlice";
import { ghsPayoutMethods } from "../../../../Wallets/const";
import {
  buildInternationalMerchantAccountPayload,
  resolveSettlementPayoutAccountId,
} from "../../resolvePayoutAccount";

interface Props {
  onGoBack: (data: number) => void;
  amount?: number;
  fee?: number;
  setFee?: (data: number) => void;
  date?: string;
  beneficiaryName?: string;
  bankName?: string;
  accountNumber?: string;
  totalAmount?: number;
  bankCode?: string;
  currency?: string;
  rate?: number;
  id?: string;
}

const Preview = ({
  onGoBack,
  fee,
  date,
  bankCode,
  currency,
  id,
}: Props) => {
  const {currencyCode} = useParams()
  const currencyOfInterest = currencyCode?.toUpperCase() || "";
  const formData = useAppSelector((state) => state?.settlementPayout?.settlementDetails);

  const beneficiaryCurrency = formData?.destinationCurrency || currencyOfInterest;
  const currencyToBeChargedIn = formData?.sourceCurrency || currencyOfInterest;

  const ghsPayout = formData?.payoutMethod === "ghsPayout"
  const momoGhsPayout = formData?.paymentMethod !== "BANK_ACCOUNT" && ghsPayout

  const mobileMoneyProvider = ghsPayoutMethods.find(
    (provider) => provider.value === formData?.paymentMethod
  )?.label;

  const { businessId, userId } = useAppSelector((state) => state.users);

  const { wallets } = useAppSelector((state) => state?.wallets);
  const { keys } = useAppSelector((state) => state.settings);
  const encryptionKey = keys?.data?.encryptionKey;
  const publicKey = keys?.data?.publicKey;

  const successAlertContext = useContext(SuccessAlertContext);

  const { onShowAlert: onSuccess } = successAlertContext || {};

  const { onShowAlert } = useContext(NetworkErrorAlertContext);
  const { mutateAsync: requestPayoutMutation } = useRequestPayout();

  const navigate = useNavigate();
  const [paid, setPaid] = useState(false);
  const [savedBank, setSavedBank] = useState(null);
  const { users } = useAppSelector((state) => state);
  const [taxRefId, setTaxRefId] = useState(null);
  const dispatch = useAppDispatch();
  const { userObj } = users || {};
  const { accountUser } = userObj || {};
  const wallet = wallets?.find((wallet) => wallet.currency === currencyOfInterest);
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  useEffect(() => {
    let txRefId = uuidv4();
    txRefId = `SP-${txRefId}`;
    setTaxRefId(txRefId);
  }, []);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const totalLocalAmount = (formData?.amount ?? 0) + (formData?.processingFee ?? 0);

  const totalCharges =
    LocalVAPayoutMethods.includes(formData?.payoutMethod)
      ? totalLocalAmount
      : formData?.totalAmount;

  const handleSendOTP = async (user: { email: string }) => {
    try {
      setLoading(true);
      setShowOtp(true);
      await sendOTP(user);

      setLoading(false);
      window.close();
    } catch (err) {
      const errorObj = err?.response?.data;
      let errorMessage = "";

      if (errorObj && typeof errorObj.error === "string") {
        errorMessage = errorObj.error;
      } else if (errorObj && typeof errorObj.message === "string") {
        errorMessage = errorObj.message;
      } else {
        errorMessage = "Payout Failed";
      }

      setLoading(false);
      onShowAlert("", errorMessage);
    }
  };

  const makeLocalPayment = async () => {
    setLoading(true);
    const LocalPayout = {
      sourceCurrency: currencyOfInterest,
      destinationCurrency: currencyOfInterest,
      amount: Number(formData?.amount),
      requestId: taxRefId,
      accountHolderName: formData?.accountName,
      bankCode: formData?.bankCode,
      bankName: formData?.bankName,
      accountNumber: formData?.accountNumber,
      type: "corporate",
      email: accountUser.email,
      fee: formData?.processingFee ?? 0,
      narration: "settlement payout",
      wallet: wallet?.id,
      product: "VirtualAccount",
    };

    try {
      await sendFunds(LocalPayout);

      onSuccess("Payout sent  successfully");

      setTimeout(() => {
        dispatch(clearSettlementDetails());
        navigate("/settlements?=Payouts");
      }, 2000);
      setLoading(false);
    } catch (error) {
      onShowAlert("", error);
      setLoading(false);
    }
  };

  const debitInternationalWallet = async (req: string) => {
    setLoading(true);
    try {
      await debitWallet({
        fromWallet: wallet?.id,
        fee: Number(formData?.processingFee),
        walletTnxId: req,
        amount: Number(formData?.totalAmount),
        fromBalance: "settlement",
      });
      setLoading(false);
      onUpdatePayment(req);
      onSuccess("Payout sent successfully");
    } catch (error) {
      onShowAlert("", error);
      setLoading(false);
    }
  };

  const getMerchantAccounts = async () => {
    try {
      const res = await dispatch(merchantBankAccounts(userId));
      setSavedBank(res.payload);
      return res.payload;
    } catch (error) {
      throw error;
    }
  };

  const onRequestPayout = async () => {
    setLoading(true);
    try {
      const merchantAccounts = Array.isArray(savedBank)
        ? savedBank
        : await getMerchantAccounts();

      const selectedAccount = await resolveSettlementPayoutAccountId({
        existingAccounts: merchantAccounts,
        beneficiaryAccountNumber: formData?.beneficiary?.accountNumber,
        createAccount: () =>
          createUpdateBankAccount(
            buildInternationalMerchantAccountPayload({
              beneficiary: formData?.beneficiary,
              userId,
              businessId,
              currency: formData?.destinationCurrency,
            }),
          ),
        refreshAccounts: async () => {
          const res = await dispatch(merchantBankAccounts(userId));
          setSavedBank(res.payload);
          return res.payload;
        },
      });

      const requestPayload = {
        walletId: wallet?.id,
        payoutAmount: Number(formData?.totalAmount),
        selectedAccount,
        businessId,
      };

      const encryptedData = encryptAlgorithm(requestPayload, encryptionKey);

      const payload = {
        message: encryptedData,
      };

      const res = await requestPayoutMutation({ payload, token: publicKey });
      const { requestId } = res;

      if (requestId) {
        debitInternationalWallet(requestId);
      }
    } catch (error) {
      setLoading(false);
      onShowAlert(
        "",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to complete payout",
      );
    }
  };

  useEffect(() => {
    if (userId && businessId) {
      getMerchantAccounts();
    }
  }, [userId, businessId]);

  const onUpdatePayment = async (req: string) => {
    setLoading(true);
    try {
      const wireObj = {
        sourceCurrency: formData?.destinationCurrency,
        sourceAmount: formData?.totalAmount,
        destinationCurrency: settlementMethodCurrencyMap[formData?.payoutMethod],
        destinationAmount: formData?.amount,
        schedule: true,
        total_fees: formData?.processingFee,
        totalFees: formData?.transferFee,
        amountToConvert: Number((formData?.amount ?? 0) * (formData?.exchangeRate ?? 1)),
        rate: formData?.exchangeRate ?? 1,
        paid: paid,
        amountReceived:
          Number((formData?.amount ?? 0) * (formData?.exchangeRate ?? 1)) +
          Number(formData?.processingFee ?? 0),
        business: businessId,
        merchantBeneficiary: formData?.beneficiary?.id,
        transactionRef: req,
      };

      const encryptedData = encryptAlgorithm(wireObj);
      const payload = {
          message: encryptedData,
      };

      await createWirePayment(payload);
      setLoading(false);
      setPaid(true);
      setTimeout(() => {
        navigate("/settlements?=Payouts");
      }, 2000);
    } catch (error) {
      setLoading(false);
      onShowAlert("", error.response.data.message);
    }
  };

  return (
    <PayoutWrapper>
      <Heading>Review the details of your transfer</Heading>
      <motion.div
        variants={nextVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <DetailsBox>
          <DetailsSectionHeader>
            <SectionTitle>Transaction details</SectionTitle>
            <EditButton onClick={() => onGoBack(0)}>
              <span>Edit</span>
            </EditButton>
          </DetailsSectionHeader>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Beneficiary’s amount</DetailsLabel>
              <DetailsValue>
                {formatCurrencyCodeAmount(beneficiaryCurrency, formData?.amount)}
              </DetailsValue>
            </DetailsRow>
            {formData?.exchangeRate && (
              <DetailsRow>
                <DetailsLabel>Exchange rate</DetailsLabel>
                <DetailsValue>
                  {formatCurrencyCodeAmount(beneficiaryCurrency, 1)} = {" "}
                  {formatCurrencyCodeAmount(currencyToBeChargedIn, formData?.exchangeRate)}
                </DetailsValue>
              </DetailsRow>
            )}
            <DetailsRow>
              <DetailsLabel>Processing fee</DetailsLabel>
              <DetailsValue>
                {formatCurrencyCodeAmount(
                  currencyToBeChargedIn,
                  formData?.processingFee
                )}
              </DetailsValue>
            </DetailsRow>
            <DetailsRow>
              <DetailsLabel>Total charge amount</DetailsLabel>
              <DetailsValue>
                {formatCurrencyCodeAmount(
                  currencyToBeChargedIn,
                  (
                    totalCharges
                  )?.toFixed(2)
                )}
              </DetailsValue>
            </DetailsRow>
            <DetailsRow>
              <DetailsLabel>Date</DetailsLabel>
              <DetailsValue>{date}</DetailsValue>
            </DetailsRow>
          </DetailsContent>

          <DetailsSectionHeader>
            <SectionTitle>Beneficiary details</SectionTitle>
            <EditButton onClick={() => onGoBack(1)}>
              <span>Edit</span>
            </EditButton>
          </DetailsSectionHeader>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Beneficiary name</DetailsLabel>
              <DetailsValue>{formData?.accountName || formData?.beneficiary.beneficiaryName}</DetailsValue>
            </DetailsRow>
            <DetailsRow>
              <DetailsLabel>{momoGhsPayout ? "Mobile Money Provider" : "Bank name"}</DetailsLabel>
              <DetailsValue>{momoGhsPayout ? mobileMoneyProvider : (formData?.bankName || formData?.beneficiary.bankName)}</DetailsValue>
            </DetailsRow>
            {!momoGhsPayout && (
              <DetailsRow>
                <DetailsLabel>Bank address</DetailsLabel>
                <DetailsValue>{formData?.bankAddress || "N/A"}</DetailsValue>
              </DetailsRow>
            )}
            <DetailsRow>
              <DetailsLabel>Account number</DetailsLabel>
              <DetailsValue>{formData?.accountNumber || formData?.beneficiary.accountNumber}</DetailsValue>
            </DetailsRow>
          </DetailsContent>
        </DetailsBox>
      </motion.div>

      <InputWrapper>
        <Button
          label="Continue"
          theme="primary"
          height="46px"
          onClick={() => handleSendOTP(accountUser?.email)}
        />
      </InputWrapper>

      {showOtp && (
        <WalletOTP
          setVerifyOTP={setShowOtp}
          makePayment={LocalVAPayoutMethods.includes(formData?.payoutMethod) ? makeLocalPayment : onRequestPayout}
        />
      )}

      <Loader isLoading={loading} />
    </PayoutWrapper>
  );
};

export default Preview;
